import { Outlet, createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";

import { sdk } from "@/api/sdk";
import { SiteConfigProvider } from "@/features/site/hooks/use-site-config";
import type { PublicDirectory, TrainerPageConfig } from "@/features/site/types";
import { trainerPageHead, type TrainerLoaderData } from "@/features/site/utils/trainer-head";
import { reportApiOutage } from "@/lib/api-outage";
import { localizePath } from "@/lib/locale";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * One trainer's public page. Every page of the template sits under this one, which resolves the
 * slug in the URL to the trainer behind it and puts their own facts - phone, address, opening
 * hours, the numbers on the home page - in front of the sections.
 *
 * Only the API saying 404 makes this a 404: a slug nobody owns, or a page its trainer has not
 * published yet. Anything else is the request failing, not the trainer missing. On the server that
 * hands the page to the browser, which asks again on its own; in the browser it is an error, so
 * the visitor sees that something broke rather than being told the trainer does not exist.
 *
 * Anything that is not a slug is a 404 before any request is made, so a crafted URL never reaches
 * the API. An address the trainer has moved away from comes back with the current one, and the
 * visitor is sent there with a permanent redirect, sub-page and query included.
 */
const SLUG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const Route = createFileRoute("/(public)/$trainerSlug")({
  // Its cover photo is the link preview, so the platform's card stays out (see lib/seo.ts).
  staticData: { ownSocialImage: true },
  head: ({ loaderData, matches }) => trainerPageHead({ loaderData, matches }),
  loader: async ({ params, location }): Promise<TrainerLoaderData> => {
    const slug = params.trainerSlug.toLowerCase();
    if (slug.length < 3 || slug.length > 64 || !SLUG_SHAPE.test(slug)) throw notFound({ routeId: Route.id });

    const { data, status } = await sdk.t.$slug.$get
      .setParams({ slug: encodeURIComponent(slug) })
      // The trainer's own calendarDays decides the span, so the loader names none.
      .send({ queryParams: {} });
    if (data) {
      const { page: stored, ...directory } = data;
      const page = stored as TrainerPageConfig;
      if (page.slug !== params.trainerSlug) {
        const rest = location.pathname.slice(`/${params.trainerSlug}`.length);
        throw redirect({
          href: `${localizePath(`/${page.slug}${rest}`, getLocale())}${location.searchStr}`,
          statusCode: 301,
        });
      }
      return {
        page,
        business: { name: data.business.name, logoUrl: data.business.logoUrl },
        directory: directory as PublicDirectory,
        crumb: data.business.name,
      };
    }
    if (status === 404) throw notFound({ routeId: Route.id });
    if (typeof window === "undefined") {
      reportApiOutage();
      return null;
    }
    throw new Error(`Could not load this trainer's page (status ${status ?? "none"})`);
  },
  component: TrainerSiteLayout,
});

function TrainerSiteLayout() {
  // Named by hand: with `head` reading the loader's data too, the router infers it as `never` here.
  const loaded: TrainerLoaderData = Route.useLoaderData();
  const router = useRouter();

  // The server could not reach the API; run the loader again here, where the request is the browser's.
  useEffect(() => {
    if (!loaded) void router.invalidate();
  }, [loaded, router]);

  if (!loaded) return null;

  return (
    <SiteConfigProvider page={loaded.page} business={loaded.business} directory={loaded.directory}>
      <Outlet />
    </SiteConfigProvider>
  );
}
